import React, { useState } from "react";
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from "@mui/material";
import { LocalizationProvider, PickersDay, DateCalendar } from "@mui/x-date-pickers";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import isSameDay from 'dayjs/plugin/isSameOrAfter';
import { addRecommendCreator } from "./campaign-repository";

export default function AddRecommendationPopup({ openRecommendDialog, setOpenRecommendDialog, recommendForm, setRecommendForm, productionStartDate, productionEndDate, campaignId, setChnagesDone, confirmRecommendation }) {
    const [error, setError] = useState();

    dayjs.extend(isSameDay);
    const handleAddRecommendation = () => {
        const updatedPhoneNumber = recommendForm.phoneNumber?.startsWith('91')
            ? recommendForm.phoneNumber
            : `91${recommendForm.phoneNumber?.replace(/^0+/, '')}`;

        const updatedForm = {
            ...recommendForm,
            phoneNumber: updatedPhoneNumber,
        };
        addRecommendCreator(updatedForm, campaignId, confirmRecommendation)
            .then((response) => {
                if (response?.error) {
                    setError(response.error);
                } else {
                    setChnagesDone(Math.random().toString(36).substring(7));
                    setRecommendForm({ phoneNumber: null, availabilityDate: null, whyRecommend: null, refLink: null });
                    setError('');
                    setOpenRecommendDialog(false);
                }
            })
            .catch((err) => {
                setError('Failed to add recommend creator. Please try again.');
            });
    };

    return (
        <Dialog open={openRecommendDialog} onClose={() => setOpenRecommendDialog(false)} maxWidth="sm" fullWidth>
            <DialogTitle>{confirmRecommendation ? 'Edit Recommended Creator' : 'Add Recommended Creator'}</DialogTitle>
            {error &&
                <Alert severity="error" sx={{ margin: '0px 24px 0px 24px' }}>
                    {error}
                </Alert>
            }
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <Typography variant="body2">
                    Phone Number<span style={{ color: 'red' }}>*</span>
                </Typography>
                <TextField
                    disabled={confirmRecommendation}
                    name="phoneNumber"
                    placeholder="Enter 10-digit number"
                    value={recommendForm.phoneNumber}
                    onChange={(e) => {
                        const digitsOnly = e.target.value.replace(/\D/g, '');
                        setRecommendForm({ ...recommendForm, phoneNumber: digitsOnly });
                    }}
                    inputProps={{ maxLength: 10 }}
                    error={
                        recommendForm.phoneNumber !== null && recommendForm.phoneNumber !== "" && !/^[6-9]\d{9}$/.test(recommendForm.phoneNumber)
                    }
                    helperText={
                        recommendForm.phoneNumber !== null && recommendForm.phoneNumber !== "" && !/^[6-9]\d{9}$/.test(recommendForm.phoneNumber)
                            ? 'Phone must start with 6, 7, 8, or 9 and be 10 digits'
                            : ''
                    }
                />

                <Box
                    sx={{
                        '.MuiPickersLayout-root': {
                            maxHeight: 360,
                            overflow: 'hidden',
                        },
                    }}
                >
                    <Typography variant="body2">
                        Select Availability Dates <span style={{ color: 'red' }}>*</span> (min&nbsp;4)
                    </Typography>

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DateCalendar
                            referenceDate={productionStartDate}
                            minDate={productionStartDate}
                            maxDate={productionEndDate}
                            onChange={(date) => {
                                const selected = dayjs(date).format('YYYY-MM-DD');

                                const exists = recommendForm.availabilityDates?.some(d =>
                                    dayjs(d).isSame(selected, 'day')
                                );

                                const updatedDates = exists
                                    ? recommendForm.availabilityDates.filter(d => !dayjs(d).isSame(selected, 'day'))
                                    : [...(recommendForm.availabilityDates || []), selected];

                                setRecommendForm({ ...recommendForm, availabilityDates: updatedDates });
                            }}
                            slots={{
                                day: (props) => {
                                    const isSelected = recommendForm.availabilityDates?.some(d =>
                                        dayjs(d).isSame(props.day, 'day')
                                    );

                                    return (
                                        <PickersDay
                                            {...props}
                                            selected={false}
                                            disableRipple
                                            disableFocusRipple
                                            sx={{
                                                ...(isSelected && {
                                                    backgroundColor: '#e563a4',
                                                    color: '#fff',
                                                    '&:hover': {
                                                        backgroundColor: '#e326a4',
                                                    },
                                                    '&.Mui-focusVisible': {
                                                        outline: 'none',
                                                        backgroundColor: '#e563a4',
                                                    },
                                                    '&:focus': {
                                                        outline: 'none',
                                                        backgroundColor: '#e563a4',
                                                    },
                                                }),
                                            }}
                                        />
                                    );
                                }
                            }}
                        />
                    </LocalizationProvider>
                </Box>

                <Typography variant="body2">
                    {confirmRecommendation ? (
                        <>
                            Why Recommended <span style={{ color: 'red' }}>*</span>
                        </>
                    ) : (
                        'Why Recommended (Optional)'
                    )}
                </Typography>
                <TextField
                    placeholder="Explain why you recommend this creator"
                    multiline
                    minRows={3}
                    value={recommendForm.whyRecommend}
                    onChange={(e) => setRecommendForm({ ...recommendForm, whyRecommend: e.target.value })}
                    error={recommendForm.whyRecommend !== null && recommendForm.whyRecommend !== "" && recommendForm.whyRecommend.length > 0 && recommendForm.whyRecommend.length < 2}
                    helperText={
                        recommendForm.whyRecommend !== null && recommendForm.whyRecommend !== "" && recommendForm.whyRecommend.length > 0 && recommendForm.whyRecommend.length < 2
                            ? 'Minimum 2 characters required'
                            : ''
                    }
                />

                <Typography variant="body2">
                    {confirmRecommendation ? (
                        <>
                            Reference Link <span style={{ color: 'red' }}>*</span>
                        </>
                    ) : (
                        'Reference Link (Optional)'
                    )}
                </Typography>
                <TextField
                    placeholder="https://example.com"
                    value={recommendForm.refLink}
                    onChange={(e) => setRecommendForm({ ...recommendForm, refLink: e.target.value })}
                    error={
                        recommendForm.refLink !== null && recommendForm.refLink !== "" && recommendForm.refLink.length > 0 &&
                        !/^https?:\/\/[\w-]+(\.[\w-]+)+[/#?]?.*$/.test(recommendForm.refLink)
                    }
                    helperText={
                        recommendForm.refLink !== null && recommendForm.refLink !== "" && recommendForm.refLink.length > 0 &&
                            !/^https?:\/\/[\w-]+(\.[\w-]+)+[/#?]?.*$/.test(recommendForm.refLink)
                            ? 'Enter a valid URL'
                            : ''
                    }
                />
            </DialogContent>

            <DialogActions>
                <Button onClick={() => { setOpenRecommendDialog(false); setRecommendForm({ phoneNumber: null, availabilityDate: null, whyRecommend: null, refLink: null });}}>Cancel</Button>
                <Button
                    disabled={
                        confirmRecommendation
                            ? (
                                !/^[6-9]\d{9}$/.test(recommendForm.phoneNumber) ||
                                !recommendForm.availabilityDates || recommendForm.availabilityDates.length < 4 ||
                                !recommendForm.whyRecommend || recommendForm.whyRecommend.length < 2 ||
                                !recommendForm.refLink || !/^https?:\/\/[\w-]+(\.[\w-]+)+[/#?]?.*$/.test(recommendForm.refLink)
                            )
                            : (
                                !/^[6-9]\d{9}$/.test(recommendForm.phoneNumber) ||
                                !recommendForm.availabilityDates || recommendForm.availabilityDates.length < 4 ||
                                (recommendForm.whyRecommend && recommendForm.whyRecommend.length < 2) ||
                                (recommendForm.refLink && !/^https?:\/\/[\w-]+(\.[\w-]+)+[/#?]?.*$/.test(recommendForm.refLink))
                            )
                    }
                    onClick={handleAddRecommendation}
                >
                    Submit
                </Button>
            </DialogActions>
        </Dialog>
    )
}